import {
  CheckIcon,
  CopySimpleIcon,
  DownloadSimpleIcon,
  ThumbsDownIcon,
  ThumbsUpIcon,
} from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { Button } from "react-aria-components";
import { IconButton } from "@/components/ui/icon-button";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";

export interface ChatResponseControlsProps {
  /** Response timestamp; renders a short date with the full timestamp in a tooltip. */
  createdAt?: Date | string;
  /** Return `false` when copy failed so the UI does not show a success state. */
  onCopy?: () => boolean | undefined | Promise<boolean | undefined>;
  onThumbsUp?: () => void;
  onThumbsDown?: () => void;
  onDownloadPdf?: () => void;
  /** When true, PDF control shows pending UI (e.g. from `useDownloadHtmlAsPdf`). */
  isPdfGenerating?: boolean;
}

const COPY_FEEDBACK_DURATION_MS = 2000;
const PDF_DOWNLOAD_LABEL = "Download as PDF";
const PDF_GENERATING_LABEL = "Generating PDF…";

const formatShortDate = (date: Date) =>
  date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year:
      date.getFullYear() === new Date().getFullYear() ? undefined : "numeric",
  });

/** e.g. "September 30, 2026 11:11 AM CT" in the viewer's timezone. */
const formatFullTimestamp = (date: Date) =>
  `${date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  })} ${date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "shortGeneric",
  })}`;

const ChatResponseTimestamp = ({ date }: { date: Date }) => (
  <TooltipTrigger>
    <Button
      className="cursor-default rounded-sm px-1.5 text-sm text-gray-500 outline-none transition-opacity
        data-focus-visible:ring-2 data-focus-visible:ring-blue-750
        [@media(hover:hover)]:opacity-0
        [@media(hover:hover)]:group-hover/chat-turn:opacity-100
        [@media(hover:hover)]:group-focus-within/chat-turn:opacity-100"
    >
      <time dateTime={date.toISOString()}>{formatShortDate(date)}</time>
    </Button>
    <Tooltip placement="top" offset={10} crossOffset={10}>
      {formatFullTimestamp(date)}
    </Tooltip>
  </TooltipTrigger>
);

const ChatResponseControls = ({
  createdAt,
  onCopy,
  onThumbsUp,
  onThumbsDown,
  onDownloadPdf,
  isPdfGenerating = false,
}: ChatResponseControlsProps) => {
  const [isCopied, setIsCopied] = useState(false);
  const hasCopy = typeof onCopy === "function";
  const hasThumbsUp = typeof onThumbsUp === "function";
  const hasThumbsDown = typeof onThumbsDown === "function";
  const hasDownloadPdf = typeof onDownloadPdf === "function";
  const parsedCreatedAt = createdAt == null ? null : new Date(createdAt);
  const createdAtDate =
    parsedCreatedAt != null && !Number.isNaN(parsedCreatedAt.getTime())
      ? parsedCreatedAt
      : null;

  useEffect(() => {
    if (!isCopied) {
      return;
    }
    const timeoutId = setTimeout(
      () => setIsCopied(false),
      COPY_FEEDBACK_DURATION_MS,
    );
    return () => clearTimeout(timeoutId);
  }, [isCopied]);

  const handleCopyPress = async () => {
    if (!onCopy) {
      return;
    }
    const result = await onCopy();
    if (result !== false) {
      setIsCopied(true);
    }
  };

  const handlePdfPress = () => {
    if (isPdfGenerating) {
      return;
    }
    onDownloadPdf?.();
  };

  if (
    !hasCopy &&
    !hasThumbsUp &&
    !hasThumbsDown &&
    !hasDownloadPdf &&
    createdAtDate == null
  ) {
    return null;
  }

  return (
    <div className="flex items-center gap-1 text-gray-500">
      {hasCopy ? (
        <IconButton
          icon={
            isCopied ? (
              <CheckIcon className="text-gray-700" />
            ) : (
              <CopySimpleIcon className="text-gray-700" />
            )
          }
          variant="ghost"
          size="md"
          aria-label="Copy response"
          onPress={() => void handleCopyPress()}
          tooltipLabel={isCopied ? "Copied" : "Copy"}
          tooltipPosition="bottom"
        />
      ) : null}

      {hasThumbsUp ? (
        <IconButton
          icon={<ThumbsUpIcon className="text-gray-700" />}
          variant="ghost"
          size="md"
          aria-label="Give positive feedback"
          onPress={onThumbsUp}
          tooltipLabel="Give positive feedback"
          tooltipPosition="bottom"
        />
      ) : null}

      {hasThumbsDown ? (
        <IconButton
          icon={<ThumbsDownIcon className="text-gray-700" />}
          variant="ghost"
          size="md"
          aria-label="Give negative feedback"
          onPress={onThumbsDown}
          tooltipLabel="Give negative feedback"
          tooltipPosition="bottom"
        />
      ) : null}

      {hasDownloadPdf ? (
        <IconButton
          icon={<DownloadSimpleIcon className="text-gray-700" />}
          variant="ghost"
          size="md"
          aria-label={
            isPdfGenerating ? PDF_GENERATING_LABEL : PDF_DOWNLOAD_LABEL
          }
          onPress={handlePdfPress}
          tooltipLabel={
            isPdfGenerating ? PDF_GENERATING_LABEL : PDF_DOWNLOAD_LABEL
          }
          tooltipPosition="bottom"
          isPending={isPdfGenerating}
          isDisabled={isPdfGenerating}
        />
      ) : null}

      {createdAtDate != null ? (
        <ChatResponseTimestamp date={createdAtDate} />
      ) : null}
    </div>
  );
};

export default ChatResponseControls;
